import ResourceRequestFilters from "./ResourceRequestFilters";
import { Globe, Server } from "lucide-react";
import { Tabs, TabsList, TabsTrigger } from "../../ui/tabs";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { useToast } from "../../../hooks/use-toast";
import OnPremResourceRequests from "./Tabs/OnPremResourceRequests";
import { WebUrlRequestDetails } from "./Tabs/WebUrlRequestDetails";
import { AzureUserRequestDetails } from "./Tabs/AzureUserRequestDetails";
import { GCPRequestDetails } from "./Tabs/GCPRequestDetails";
import { K8RequestDetails } from "./Tabs/K8RequestDetails";
import {
  downloadOnPremRequestReport,
  getAdminNotificationCountAPI,
  getAWSNotificationCountAPI,
  getAzureNotificationCountAPI,
  getGCPNotificationCountAPI,
  getK8NotificationCountAPI,
  getWebURLNotificationCountAPI,
} from "./Api/resourcerequestapi";
import { myLocalStorage } from "../../StorageHelper";
import useUserStore from "../../../services/userStore";
import AWSResourceRequests from "./Tabs/AWSResourceRequests";
import { FaGoogle } from "react-icons/fa6";
import { SiAmazon, SiKubernetes } from "react-icons/si";
import { VscAzure } from "react-icons/vsc";

import "../CloudAnalytics/CloudEntityAnalytics.css";
import "./ResourceRequests.css";

type PlatformType = "onprem" | "aws" | "azure" | "gcp" | "k8s" | "weburl";

interface PlatformConfig {
  id: PlatformType;
  name: string;
  icon: any;
  color: string;
  description: string;
}

const platformConfigs: PlatformConfig[] = [
  {
    id: "onprem",
    name: "Endpoints",
    icon: Server,
    color: "text-slate-600",
    description: "On-premises infrastructure",
  },
  {
    id: "aws",
    name: "AWS",
    icon: SiAmazon,
    color: "text-orange-600",
    description: "Amazon Web Services",
  },
  {
    id: "azure",
    name: "Azure",
    icon: VscAzure,
    color: "text-blue-600",
    description: "Microsoft Azure",
  },
  {
    id: "gcp",
    name: "GCP",
    icon: FaGoogle,
    color: "text-green-600",
    description: "Google Cloud Platform",
  },
  {
    id: "k8s",
    name: "K8s",
    icon: SiKubernetes,
    color: "text-purple-600",
    description: "Kubernetes clusters",
  },
  {
    id: "weburl",
    name: "WebURL",
    icon: Globe,
    color: "text-indigo-600",
    description: "Web applications",
  },
];

const emptyPendingCounts: Record<PlatformType, number> = {
  onprem: 0,
  aws: 0,
  azure: 0,
  gcp: 0,
  k8s: 0,
  weburl: 0,
};

const requestedPlatform = (state: unknown): PlatformType | null => {
  const platform = (state as { resourceRequestPlatform?: string } | null)?.resourceRequestPlatform;
  return platformConfigs.find((config) => config.id === platform)?.id ?? null;
};

const normalizeCount = (value: unknown) => {
  const count = Number(value);
  return Number.isFinite(count) ? count : 0;
};

export default function ResourceRequestDetails() {
  const location = useLocation();
  const navigate = useNavigate();
  const [searchTerm, setSearchTerm] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");
  const [privilegeFilter, setPrivilegeFilter] = useState("all");
  const [activePlatform, setActivePlatform] = useState<PlatformType>(
    () => requestedPlatform(location.state) ?? "onprem",
  );
  const [startDate, setStartDate] = useState<Date | undefined>(undefined);
  const [endDate, setEndDate] = useState<Date | undefined>(undefined);
  const [pendingCounts, setPendingCounts] = useState(emptyPendingCounts);
  const countsLoadingRef = useRef<{ key: string } | null>(null);
  const { toast } = useToast();
  const latestTenant = myLocalStorage.getItem("latestTenant");
  const tenantName =
    typeof latestTenant === "string"
      ? latestTenant
      : latestTenant?.tenantName || "";
  const { email, role } = useUserStore((state) => state.user);

  useEffect(() => {
    if (!(location.state as { resourceRequestPlatform?: string } | null)?.resourceRequestPlatform) return;
    const platform = requestedPlatform(location.state);
    if (platform) setActivePlatform(platform);
    navigate(location.pathname, { replace: true, state: null });
  }, [location.state, location.pathname, navigate]);

  const fetchPendingCounts = useCallback(async () => {
    const key = `${tenantName}:${email}:${role}:${privilegeFilter}`;
    if (!tenantName || !role || countsLoadingRef.current?.key === key) return;
    const request = { key };
    countsLoadingRef.current = request;
    try {
      const args: [
        string,
        string,
        string,
        string,
        string,
        string,
        number,
        string,
        string,
        string,
      ] = [
        "",
        "",
        "PENDING",
        "",
        "",
        tenantName,
        0,
        email,
        role,
        privilegeFilter,
      ];
      const [onprem, aws, azure, gcp, k8s, weburl] = await Promise.all([
        getAdminNotificationCountAPI(...args),
        getAWSNotificationCountAPI(...args),
        getAzureNotificationCountAPI(...args),
        getGCPNotificationCountAPI(...args),
        getK8NotificationCountAPI(...args),
        getWebURLNotificationCountAPI(...args),
      ]);
      if (countsLoadingRef.current !== request) return;
      setPendingCounts({
        onprem: normalizeCount(onprem),
        aws: normalizeCount(aws),
        azure: normalizeCount(azure),
        gcp: normalizeCount(gcp),
        k8s: normalizeCount(k8s),
        weburl: normalizeCount(weburl),
      });
    } finally {
      if (countsLoadingRef.current === request) countsLoadingRef.current = null;
    }
  }, [email, role, tenantName, privilegeFilter]);

  useEffect(() => {
    fetchPendingCounts();
    const timer = setInterval(fetchPendingCounts, 5000);
    return () => clearInterval(timer);
  }, [fetchPendingCounts]);

  const handleReset = () => {
    setStartDate(undefined);
    setEndDate(undefined);
    setStatusFilter("all");
    setPrivilegeFilter("all");
    setSearchTerm("");
  };

  const currentPlatformConfig = useMemo(() => {
    return (
      platformConfigs.find((p) => p.id === activePlatform) || platformConfigs[0]
    );
  }, [activePlatform]);

  const handleDownloadReport = async () => {
    toast({
      title: "Download Started",
      description: `${currentPlatformConfig.name} resource requests report is being downloaded...`,
    });
    try {
      await downloadOnPremRequestReport(
        activePlatform,
        tenantName,
        "",
        role,
        startDate ? startDate.toISOString() : "",
        endDate ? endDate.toISOString() : "",
        searchTerm,
        email,
        statusFilter,
        privilegeFilter,
      );
    } catch (error) {
      toast({
        title: "Download Failed",
        description:
          error instanceof Error
            ? error.message
            : "Unable to generate the report. Please try again.",
        variant: "destructive",
      });
    }
  };

  return (
    <div className="resource-request-page">
      <Tabs
        value={activePlatform}
        onValueChange={(value) => setActivePlatform(value as PlatformType)}
        className="resource-request-platforms"
      >
        <TabsList className="cea-tabs resource-request-tabs">
          {platformConfigs.map((platform) => {
            const IconComponent = platform.icon;
            const count = pendingCounts[platform.id];
            return (
              <TabsTrigger
                key={platform.id}
                value={platform.id}
                className="cea-tab resource-request-tab"
              >
                <IconComponent className={`w-4 h-4 ${platform.color}`} />
                {platform.name}
                {count > 0 && (
                  <span className="resource-request-count">
                    {count > 99 ? "99+" : count}
                  </span>
                )}
              </TabsTrigger>
            );
          })}
        </TabsList>
      </Tabs>
      <ResourceRequestFilters
        searchTerm={searchTerm}
        statusFilter={statusFilter}
        privilegeFilter={privilegeFilter}
        startDate={startDate}
        endDate={endDate}
        onSearchChange={setSearchTerm}
        onStatusChange={setStatusFilter}
        onPrivilegeChange={setPrivilegeFilter}
        onStartDateChange={setStartDate}
        onEndDateChange={setEndDate}
        onReset={handleReset}
        onDownloadReport={handleDownloadReport}
        showDownload={role !== "TENANT_USER"}
      />
      <div className="resource-request-content">
        {activePlatform === "onprem" && (
          <OnPremResourceRequests
            key={privilegeFilter}
            computerName={""}
            searchTerm={searchTerm}
            statusFilter={statusFilter}
            privilegeFilter={privilegeFilter}
            startDate={startDate}
            endDate={endDate}
            activeTab={activePlatform}
          />
        )}
        {activePlatform === "aws" && (
          <AWSResourceRequests
            key={privilegeFilter}
            computerName={""}
            searchTerm={searchTerm}
            statusFilter={statusFilter}
            privilegeFilter={privilegeFilter}
            startDate={startDate}
            endDate={endDate}
            activeTab={activePlatform}
          />
        )}
        {activePlatform === "azure" && (
          <AzureUserRequestDetails
            key={privilegeFilter}
            computerName={""}
            searchTerm={searchTerm}
            statusFilter={statusFilter}
            privilegeFilter={privilegeFilter}
            startDate={startDate}
            endDate={endDate}
            activeTab={activePlatform}
          />
        )}
        {activePlatform === "gcp" && (
          <GCPRequestDetails
            key={privilegeFilter}
            computerName={""}
            searchTerm={searchTerm}
            statusFilter={statusFilter}
            privilegeFilter={privilegeFilter}
            startDate={startDate}
            endDate={endDate}
            activeTab={activePlatform}
          />
        )}
        {activePlatform === "k8s" && (
          <K8RequestDetails
            key={privilegeFilter}
            computerName={""}
            searchTerm={searchTerm}
            statusFilter={statusFilter}
            privilegeFilter={privilegeFilter}
            startDate={startDate}
            endDate={endDate}
            activeTab={activePlatform}
          />
        )}
        {activePlatform === "weburl" && (
          <WebUrlRequestDetails
            key={privilegeFilter}
            computerName={""}
            searchTerm={searchTerm}
            statusFilter={statusFilter}
            privilegeFilter={privilegeFilter}
            startDate={startDate}
            endDate={endDate}
            activeTab={activePlatform}
          />
        )}
      </div>
    </div>
  );
}
